/**
 * Satış haritası (v0.1.269; Sercan, 3 Nov 2026: the old portal's "Global Satış Radarı" in Twin, "daha güzelini yap").
 * GET /sales-map for a period (Tümü, 365, 90, 30 gün) and a product family. On top four tiles (Ülke, Şehir, Sipariş,
 * Gelir) with their change; then the world map: countries filled by revenue on one sequential hue, town bubbles sized by
 * orders (square root) and coloured by their dominant family, ringed in the card colour so neighbours separate. Zoomed
 * out the towns of a country are one bubble, zoomed in they split; orders of the last 7 days pulse (not under reduced
 * motion). Wheel, drag, pinch, the buttons, double click to zoom in, right click to zoom out. A country opens a side
 * panel (a sheet under the map on a phone) with its towns, best products and newest orders. Under the map the country
 * ranking (sortable), the newest orders, the months new countries came in and the town table (also the screen reader
 * alternative). Towns only: no buyer name anywhere.
 */

import {
  SALES_MAP_CATEGORY_FILTERS,
  SALES_MAP_PERIODS,
  type SalesCategory,
  type SalesDominant,
  type SalesMapCategoryFilter,
  type SalesMapCity,
  type SalesMapCountry,
  type SalesMapPeriod,
  type SalesMapView,
  type SalesPlacePrecision,
} from '@twin/contracts';
import {
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIsDesktop } from '../app/media.js';
import { salesMapKey, useSalesMap } from '../app/sales-map.js';
import { formatIsoDay, formatNumber } from '../lib/format.js';
import { WORLD_HEIGHT, WORLD_WIDTH, world } from '../lib/world-geo.js';
import { Segmented } from '../screens/common.js';
import { Icon } from './icons.js';
import { type Transform, zoomAbout } from './region-map.js';

export const PERIOD_LABELS: Readonly<Record<SalesMapPeriod, string>> = Object.freeze({
  all: 'Tümü',
  '365': '365 gün',
  '90': '90 gün',
  '30': '30 gün',
});

export const CATEGORY_LABELS: Readonly<Record<SalesMapCategoryFilter, string>> = Object.freeze({
  all: 'Tüm ürünler',
  scan: 'Tarama / STL',
  step: 'STEP CAD',
  plan: 'DIY plan',
  pack: 'Paket',
});

export const DOMINANT_LABELS: Readonly<Record<SalesDominant, string>> = Object.freeze({
  scan: 'Tarama / STL',
  step: 'STEP CAD',
  plan: 'DIY plan',
  pack: 'Paket',
  mixed: 'Karışık',
});

const PRECISION_LABELS: Readonly<Record<SalesPlacePrecision, string>> = Object.freeze({
  city: 'şehir',
  region: 'şehir konumu bulunamadı, eyalet ortası',
  country: 'şehir konumu bulunamadı, ülke ortası',
  none: 'şehir konumu bulunamadı',
});

/** Below this zoom the towns of a country are one bubble. */
export const CLUSTER_ZOOM = 2;
/** Fill steps of the revenue scale (the lightest is "sold a little", none is the plain land colour). */
const FILL_STEPS = 5;
const IDENTITY: Transform = { k: 1, x: 0, y: 0 };
const DRAG_SLOP = 4;

const usd = (cents: number): string => `${formatNumber(Math.round(cents / 100))} $`;
const n = (value: number): string => formatNumber(value);

function addDays(day: string, days: number): string {
  const at = new Date(`${day}T00:00:00.000Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

/** Orders of the last 7 days (today included) pulse. */
export const isFresh = (lastDay: string, today: string): boolean => lastDay >= addDays(today, -6);

/** The smallest and largest revenue of the selling countries; the map's shades and the legend's ends both use it. */
export function revenueRange(countries: readonly Pick<SalesMapCountry, 'revenueCents'>[]): {
  min: number;
  max: number;
} {
  const sold = countries.map((country) => country.revenueCents).filter((cents) => cents > 0);
  return sold.length === 0 ? { min: 0, max: 0 } : { min: Math.min(...sold), max: Math.max(...sold) };
}

/**
 * The fill step of a country's revenue (1..FILL_STEPS; 0 for none) on a log scale spread between the smallest and the
 * largest selling country, so the lightest shade is the smallest market and the darkest the largest; one country (or
 * all equal) is the darkest.
 */
export function fillStep(revenueCents: number, minCents: number, maxCents: number): number {
  if (revenueCents <= 0 || maxCents <= 0) return 0;
  const low = Math.max(1, Math.min(minCents, revenueCents));
  if (maxCents <= low) return FILL_STEPS;
  const ratio = (Math.log(revenueCents) - Math.log(low)) / (Math.log(maxCents) - Math.log(low));
  return Math.min(FILL_STEPS, Math.max(1, 1 + Math.floor(ratio * (FILL_STEPS - 1) + 1e-9)));
}

/** A bubble's radius in screen units: square root of its orders against the largest bubble shown. */
export function bubbleRadius(orders: number, maxOrders: number): number {
  return 3.5 + 11 * Math.sqrt(orders / Math.max(1, maxOrders));
}

function placeText(city: Pick<SalesMapCity, 'city' | 'region'>): string {
  if (city.city === null) return 'Şehir bilinmiyor';
  return city.region === null || city.region === city.city ? city.city : `${city.city}, ${city.region}`;
}

interface Bubble {
  id: string;
  country: string;
  x: number;
  y: number;
  orders: number;
  revenueCents: number;
  dominant: SalesDominant;
  lastDay: string;
  /** Towns inside (1 for a town). */
  towns: number;
  label: string;
  tip: string[];
}

/**
 * A country bubble's family: the towns' dominant families weighted by their revenue (orders when there is none); a
 * family with 60 % of it wins, otherwise mixed (as a town is mixed).
 */
export function clusterDominant(
  towns: readonly Pick<SalesMapCity, 'dominant' | 'orders' | 'revenueCents'>[],
): SalesDominant {
  const weights = new Map<SalesDominant, number>();
  const money = towns.some((town) => town.revenueCents > 0);
  for (const town of towns) {
    weights.set(town.dominant, (weights.get(town.dominant) ?? 0) + (money ? town.revenueCents : town.orders));
  }
  const total = [...weights.values()].reduce((a, b) => a + b, 0);
  const [top] = [...weights.entries()].sort((a, b) => b[1] - a[1]);
  if (top === undefined || total === 0) return 'scan';
  return top[0] !== 'mixed' && top[1] / total >= 0.6 ? top[0] : 'mixed';
}

/** Town and country bubbles in map units; unplaced towns sit on their country's drawn centre. */
export function bubblesOf(
  view: SalesMapView,
  clustered: boolean,
  project: (lat: number, lon: number) => [number, number] | null,
  centreOf: (iso2: string) => [number, number] | null,
): Bubble[] {
  const names = new Map(view.countries.map((country) => [country.iso2, country.name]));
  const points = view.cities.flatMap((city) => {
    const point = city.lat !== null && city.lon !== null ? project(city.lat, city.lon) : centreOf(city.country);
    return point === null ? [] : [{ city, point }];
  });
  const townBubble = ({ city, point }: (typeof points)[number]): Bubble => {
    const country = names.get(city.country) ?? city.country;
    const where = `${placeText(city)}, ${country}`;
    return {
      id: city.key,
      country: city.country,
      x: point[0],
      y: point[1],
      orders: city.orders,
      revenueCents: city.revenueCents,
      dominant: city.dominant,
      lastDay: city.lastDay,
      towns: 1,
      label: `${where}: ${n(city.orders)} sipariş, ${usd(city.revenueCents)}, son sipariş ${formatIsoDay(city.lastDay)}, ${DOMINANT_LABELS[city.dominant]}${city.located ? '' : `, ${PRECISION_LABELS[city.precision]}`}`,
      tip: [
        where,
        `${n(city.orders)} sipariş · ${usd(city.revenueCents)}`,
        `Son sipariş ${formatIsoDay(city.lastDay)} · ${DOMINANT_LABELS[city.dominant]}`,
        ...(city.located ? [] : [PRECISION_LABELS[city.precision]]),
      ],
    };
  };
  if (!clustered) return points.map(townBubble);
  const groups = new Map<string, typeof points>();
  for (const entry of points) {
    const list = groups.get(entry.city.country) ?? [];
    list.push(entry);
    groups.set(entry.city.country, list);
  }
  return [...groups.entries()].map(([iso2, list]) => {
    const [only] = list;
    if (list.length === 1 && only !== undefined) return townBubble(only);
    const orders = list.reduce((sum, entry) => sum + entry.city.orders, 0);
    const towns = list.filter((entry) => entry.city.city !== null).length;
    const revenueCents = list.reduce((sum, entry) => sum + entry.city.revenueCents, 0);
    const dominant = clusterDominant(list.map((entry) => entry.city));
    const lastDay = list.reduce((max, entry) => (entry.city.lastDay > max ? entry.city.lastDay : max), '');
    const x = list.reduce((sum, entry) => sum + entry.point[0] * entry.city.orders, 0) / Math.max(1, orders);
    const y = list.reduce((sum, entry) => sum + entry.point[1] * entry.city.orders, 0) / Math.max(1, orders);
    const name = names.get(iso2) ?? iso2;
    return {
      id: `country:${iso2}`,
      country: iso2,
      x,
      y,
      orders,
      revenueCents,
      dominant,
      lastDay,
      towns,
      label: `${name}: ${n(towns)} şehir, ${n(orders)} sipariş, ${usd(revenueCents)}, son sipariş ${formatIsoDay(lastDay)}, ${DOMINANT_LABELS[dominant]}. Yakınlaştırınca şehirlere ayrılır`,
      tip: [
        name,
        `${n(towns)} şehir · ${n(orders)} sipariş · ${usd(revenueCents)}`,
        `Son sipariş ${formatIsoDay(lastDay)} · ${DOMINANT_LABELS[dominant]}`,
        'Yakınlaştır: şehirlere ayrılır',
      ],
    };
  });
}

function activate(event: KeyboardEvent, action: () => void): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    action();
  }
}

interface Tip {
  x: number;
  y: number;
  lines: string[];
}

function SalesWorldMap({
  view,
  selected,
  onSelect,
}: {
  view: SalesMapView;
  selected: string | null;
  onSelect: (iso2: string) => void;
}) {
  const geo = useMemo(() => world(), []);
  const [t, setT] = useState<Transform>(IDENTITY);
  const [tip, setTip] = useState<Tip | null>(null);
  /** The country under the pointer or keyboard focus; drawn again on top with a thick ring. */
  const [active, setActive] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const drag = useRef<{ x: number; y: number; moved: boolean; t: Transform } | null>(null);
  const pinch = useRef<{ distance: number; k: number } | null>(null);
  const [suppressClick, setSuppressClick] = useState(false);

  const byCountry = useMemo(() => new Map(view.countries.map((country) => [country.iso2, country])), [view]);
  const range = useMemo(() => revenueRange(view.countries), [view]);
  const centres = useMemo(() => new Map(geo.shapes.map((shape) => [shape.alpha2, shape.centroid])), [geo]);
  const clustered = t.k < CLUSTER_ZOOM;
  const bubbles = useMemo(
    () =>
      bubblesOf(view, clustered, geo.project, (iso2) => centres.get(iso2) ?? null).sort((a, b) => b.orders - a.orders),
    [view, clustered, geo, centres],
  );
  const maxOrders = Math.max(1, ...bubbles.map((bubble) => bubble.orders));

  // The tooltip is placed in pixels inside the frame: right of the point, flipped to the left when it would cross the
  // right edge, and clamped so no edge cuts it (a narrow phone frame included).
  useLayoutEffect(() => {
    const frame = frameRef.current;
    const el = tipRef.current;
    if (tip === null || frame === null || el === null) return;
    const fw = frame.clientWidth;
    const fh = frame.clientHeight;
    if (fw === 0 || fh === 0) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const px = (tip.x / 100) * fw;
    const py = (tip.y / 100) * fh;
    const pad = 4;
    let left = px + 12;
    if (left + w > fw - pad) left = px - 12 - w;
    left = Math.max(pad, Math.min(left, fw - w - pad));
    const top = Math.max(pad, Math.min(py - h / 2, fh - h - pad));
    el.style.left = `${Math.round(left)}px`;
    el.style.top = `${Math.round(top)}px`;
    el.style.visibility = 'visible';
  }, [tip]);

  useEffect(() => {
    const svg = svgRef.current;
    if (svg === null) return;
    const handler = (event: globalThis.WheelEvent) => {
      event.preventDefault();
      const rect = svg.getBoundingClientRect();
      if (rect.width === 0) return;
      const scale = WORLD_WIDTH / rect.width;
      setT((prev) =>
        zoomAbout(
          prev,
          Math.exp(-event.deltaY * 0.0018),
          (event.clientX - rect.left) * scale,
          (event.clientY - rect.top) * scale,
        ),
      );
    };
    svg.addEventListener('wheel', handler, { passive: false });
    return () => svg.removeEventListener('wheel', handler);
  }, []);

  const toMap = (clientX: number, clientY: number): [number, number] => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return [clientX, clientY];
    const scale = WORLD_WIDTH / rect.width;
    return [(clientX - rect.left) * scale, (clientY - rect.top) * scale];
  };

  const onPointerDown = (event: PointerEvent<SVGSVGElement>) => {
    if (event.button === 2) return;
    const [px, py] = toMap(event.clientX, event.clientY);
    pointers.current.set(event.pointerId, { x: px, y: py });
    // The pointer is captured only once a drag or a pinch starts: a capture from the first press would send the
    // click to the map instead of the country or bubble under it.
    if (pointers.current.size === 1) {
      drag.current = { x: px, y: py, moved: false, t };
      pinch.current = null;
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      if (a && b) pinch.current = { distance: Math.hypot(a.x - b.x, a.y - b.y), k: t.k };
      drag.current = null;
      for (const id of pointers.current.keys()) event.currentTarget.setPointerCapture?.(id);
    }
  };

  const onPointerMove = (event: PointerEvent<SVGSVGElement>) => {
    if (!pointers.current.has(event.pointerId)) return;
    const [px, py] = toMap(event.clientX, event.clientY);
    pointers.current.set(event.pointerId, { x: px, y: py });
    if (pointers.current.size === 2 && pinch.current) {
      const [a, b] = [...pointers.current.values()];
      if (!a || !b || pinch.current.distance === 0) return;
      const target = pinch.current.k * (Math.hypot(a.x - b.x, a.y - b.y) / pinch.current.distance);
      setT((prev) => zoomAbout(prev, target / prev.k, (a.x + b.x) / 2, (a.y + b.y) / 2));
      return;
    }
    const d = drag.current;
    if (d === null) return;
    const dx = px - d.x;
    const dy = py - d.y;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_SLOP) return;
    if (!d.moved) event.currentTarget.setPointerCapture?.(event.pointerId);
    d.moved = true;
    setSuppressClick(true);
    setTip(null);
    setT({ k: d.t.k, x: d.t.x + dx, y: d.t.y + dy });
  };

  const onPointerUp = (event: PointerEvent<SVGSVGElement>) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) {
      drag.current = null;
      setTimeout(() => setSuppressClick(false), 0);
    }
  };

  const onDoubleClick = (event: MouseEvent<SVGSVGElement>) => {
    const [px, py] = toMap(event.clientX, event.clientY);
    setT((prev) => zoomAbout(prev, 2, px, py));
  };

  const onContextMenu = (event: MouseEvent<SVGSVGElement>) => {
    event.preventDefault();
    const [px, py] = toMap(event.clientX, event.clientY);
    setT((prev) => zoomAbout(prev, 0.5, px, py));
  };

  const pick = (action: () => void) => {
    if (!suppressClick) action();
  };

  const zoomBy = (factor: number) => setT((prev) => zoomAbout(prev, factor, WORLD_WIDTH / 2, WORLD_HEIGHT / 2));
  const showTip = (x: number, y: number, lines: string[]) =>
    setTip({ x: ((t.x + t.k * x) / WORLD_WIDTH) * 100, y: ((t.y + t.k * y) / WORLD_HEIGHT) * 100, lines });
  const inv = 1 / t.k;

  return (
    <div className="sm-map">
      <fieldset className="rm__tools sm-map__tools">
        <legend className="sr-only">Harita denetimleri</legend>
        <button type="button" className="rm__btn" aria-label="Yakınlaştır" onClick={() => zoomBy(1.6)}>
          <Icon name="plus" size={16} />
        </button>
        <button type="button" className="rm__btn" aria-label="Uzaklaştır" onClick={() => zoomBy(1 / 1.6)}>
          <Icon name="minus" size={16} />
        </button>
        <button type="button" className="rm__btn rm__btn--text" onClick={() => setT(IDENTITY)}>
          Sığdır
        </button>
        <span className="rm__zoom mono" aria-live="polite">
          {t.k.toFixed(1)}x · {clustered ? 'ülkeler' : 'şehirler'}
        </span>
      </fieldset>
      <div className="sm-map__frame" ref={frameRef}>
        {/* biome-ignore lint/a11y/useSemanticElements: an SVG map; the countries and bubbles inside are the controls. */}
        <svg
          ref={svgRef}
          className={`sm-map__svg${drag.current?.moved ? ' sm-map__svg--dragging' : ''}`}
          viewBox={`0 0 ${WORLD_WIDTH} ${WORLD_HEIGHT}`}
          role="group"
          aria-label={`Satış haritası: ${n(view.countries.length)} ülke, ${n(view.cities.length)} şehir. Ülkeler ve şehirler düğmedir; liste tablosu haritanın altında.`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
        >
          <g transform={`translate(${t.x} ${t.y}) scale(${t.k})`}>
            <path className="sm-sphere" d={geo.outline} style={{ strokeWidth: inv }} />
            {geo.shapes.map((shape) => {
              const country = byCountry.get(shape.alpha2);
              const step = country === undefined ? 0 : fillStep(country.revenueCents, range.min, range.max);
              const on = selected === shape.alpha2;
              const cls = `sm-land sm-fill-${step}${on ? ' sm-land--on' : ''}`;
              if (country === undefined) {
                return (
                  <path
                    key={`${shape.alpha2}:${shape.name}`}
                    className={cls}
                    d={shape.d}
                    style={{ strokeWidth: 0.5 * inv }}
                  />
                );
              }
              const label = `${country.name}: ${n(country.orders)} sipariş, ${usd(country.revenueCents)}, ${n(country.cities)} şehir`;
              return (
                // biome-ignore lint/a11y/useSemanticElements: an SVG country; a button element cannot live inside <svg>.
                <path
                  key={`${shape.alpha2}:${shape.name}`}
                  className={`${cls} sm-land--sold`}
                  d={shape.d}
                  style={{ strokeWidth: (on ? 1.6 : 0.6) * inv }}
                  role="button"
                  tabIndex={0}
                  aria-pressed={on}
                  aria-label={label}
                  onClick={() => pick(() => onSelect(shape.alpha2))}
                  onKeyDown={(event) => activate(event, () => onSelect(shape.alpha2))}
                  onPointerEnter={() => {
                    setActive(shape.alpha2);
                    showTip(shape.centroid[0], shape.centroid[1], [
                      country.name,
                      `${n(country.orders)} sipariş · ${usd(country.revenueCents)}`,
                      `${n(country.cities)} şehir · son ${formatIsoDay(country.lastDay)}`,
                    ]);
                  }}
                  onPointerLeave={() => {
                    setActive(null);
                    setTip(null);
                  }}
                  onFocus={() => {
                    setActive(shape.alpha2);
                    showTip(shape.centroid[0], shape.centroid[1], [
                      country.name,
                      `${n(country.orders)} sipariş · ${usd(country.revenueCents)}`,
                    ]);
                  }}
                  onBlur={() => {
                    setActive(null);
                    setTip(null);
                  }}
                />
              );
            })}
            {/* The selected and the hovered or focused country drawn again above the land, so neighbours drawn later
                cannot cover their ring: 2 screen px for hover and focus, 2.5 for the selected one. */}
            <g className="sm-rings">
              {geo.shapes
                .filter((shape) => shape.alpha2 === selected || shape.alpha2 === active)
                .map((shape) => (
                  <path
                    key={`ring:${shape.alpha2}:${shape.name}`}
                    className={`sm-ring${shape.alpha2 === active ? ' sm-ring--active' : ''}${shape.alpha2 === selected ? ' sm-ring--on' : ''}`}
                    d={shape.d}
                    style={{ strokeWidth: (shape.alpha2 === selected ? 2.5 : 2) * inv }}
                  />
                ))}
            </g>
            {bubbles.map((bubble) => {
              const r = bubbleRadius(bubble.orders, maxOrders) * inv;
              const fresh = isFresh(bubble.lastDay, view.today);
              const on = selected === bubble.country;
              return (
                // biome-ignore lint/a11y/useSemanticElements: an SVG bubble; a button element cannot live inside <svg>.
                <g
                  key={bubble.id}
                  className={`sm-bubble sm-bubble--${bubble.dominant}${on ? ' sm-bubble--on' : ''}`}
                  transform={`translate(${bubble.x} ${bubble.y})`}
                  role="button"
                  tabIndex={0}
                  aria-label={bubble.label}
                  onClick={() => pick(() => onSelect(bubble.country))}
                  onKeyDown={(event) => activate(event, () => onSelect(bubble.country))}
                  onPointerEnter={() => showTip(bubble.x, bubble.y, bubble.tip)}
                  onPointerLeave={() => setTip(null)}
                  onFocus={() => showTip(bubble.x, bubble.y, bubble.tip)}
                  onBlur={() => setTip(null)}
                >
                  {fresh ? <circle className="sm-pulse" r={r} style={{ strokeWidth: 1.5 * inv }} /> : null}
                  <circle className="sm-bubble__dot" r={r} style={{ strokeWidth: 2 * inv }} />
                  {bubble.towns > 1 && r * t.k >= 9 ? (
                    <text className="sm-bubble__n" style={{ fontSize: 9 * inv }} dy={3 * inv}>
                      {bubble.towns}
                    </text>
                  ) : null}
                </g>
              );
            })}
          </g>
        </svg>
        {tip === null ? null : (
          <div ref={tipRef} className="sm-tip" style={{ left: `${tip.x}%`, top: `${tip.y}%` }} aria-hidden="true">
            {tip.lines.map((line, index) => (
              <span key={line} className={index === 0 ? 'sm-tip__title' : undefined}>
                {line}
              </span>
            ))}
          </div>
        )}
      </div>
      <p className="sm-hint">
        Tekerlek, çift tıklama ya da iki parmakla yakınlaş; sağ tık uzaklaştırır; sürükleyerek kaydır. Ülkeye dokununca
        ayrıntısı açılır.
      </p>
    </div>
  );
}

function Legend({ view }: { view: SalesMapView }) {
  const { min, max } = revenueRange(view.countries);
  return (
    <div className="sm-legend">
      <ul className="sm-legend__kinds" aria-label="Şehir rengi: baskın ürün türü">
        {(['scan', 'step', 'plan', 'pack', 'mixed'] as const).map((kind) => (
          <li key={kind}>
            <span className={`sm-swatch sm-swatch--${kind}`} aria-hidden="true" />
            {DOMINANT_LABELS[kind]}
          </li>
        ))}
        <li>
          <span className="sm-swatch sm-swatch--fresh" aria-hidden="true" />
          Son 7 gün
        </li>
      </ul>
      <div className="sm-legend__scale">
        <span className="sm-legend__label">Ülke rengi: gelir</span>
        <span className="sm-scale" aria-hidden="true">
          {Array.from({ length: FILL_STEPS }, (_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed scale steps.
            <span key={index} className={`sm-scale__step sm-fill-bg-${index + 1}`} />
          ))}
        </span>
        <span className="sm-legend__ends mono">{view.countries.length === 0 ? '-' : `${usd(min)} → ${usd(max)}`}</span>
        <span className="sm-legend__label">Daire büyüklüğü: sipariş sayısı</span>
      </div>
    </div>
  );
}

function CountryPanel({
  view,
  iso2,
  onClose,
  onSelect,
}: {
  view: SalesMapView;
  iso2: string | null;
  onClose: () => void;
  onSelect: (iso2: string) => void;
}) {
  const country = iso2 === null ? undefined : view.countries.find((entry) => entry.iso2 === iso2);
  if (country === undefined) {
    return (
      <aside id="sm-side" className="sm-side au-card" aria-label="Ülke ayrıntısı">
        <h3 className="au-card__name">En çok satan ülkeler</h3>
        <p className="au-card__purpose">
          Haritada ya da listede bir ülkeye dokun: şehirleri, ürünleri ve son siparişleri burada açılır.
        </p>
        <ol className="sm-side__list">
          {view.countries.slice(0, 6).map((entry) => (
            <li key={entry.iso2}>
              <button type="button" className="sm-linkish" onClick={() => onSelect(entry.iso2)}>
                {entry.name}
              </button>
              <span className="mono">
                {n(entry.orders)} · {usd(entry.revenueCents)}
              </span>
            </li>
          ))}
        </ol>
      </aside>
    );
  }
  const towns = view.cities.filter((city) => city.country === country.iso2);
  const orders = view.recentOrders.filter((order) => order.country === country.iso2).slice(0, 5);
  const share = view.totals.revenueCents > 0 ? country.revenueCents / view.totals.revenueCents : 0;
  return (
    <aside id="sm-side" className="sm-side au-card" aria-label={`${country.name} ayrıntısı`}>
      <div className="au-card__head">
        <h3 className="au-card__name">{country.name}</h3>
        <button type="button" className="rm__btn" aria-label="Ülke ayrıntısını kapat" onClick={onClose}>
          <Icon name="x" size={16} />
        </button>
      </div>
      <dl className="sm-side__nums">
        <div>
          <dt>Sipariş</dt>
          <dd className="mono">{n(country.orders)}</dd>
        </div>
        <div>
          <dt>Gelir</dt>
          <dd className="mono">{usd(country.revenueCents)}</dd>
        </div>
        <div>
          <dt>Pay</dt>
          <dd className="mono">%{formatNumber(Math.round(share * 100))}</dd>
        </div>
        <div>
          <dt>İlk / son</dt>
          <dd className="mono">
            {formatIsoDay(country.firstDay)} / {formatIsoDay(country.lastDay)}
          </dd>
        </div>
      </dl>
      <h4 className="sm-side__h">Şehirler ({n(country.cities)})</h4>
      <ul className="sm-side__list">
        {towns.slice(0, 12).map((city) => (
          <li key={city.key}>
            <span>
              <span className={`sm-swatch sm-swatch--${city.dominant}`} aria-hidden="true" />
              {placeText(city)}
              {city.located || city.city === null ? null : <span className="sm-muted"> · konum yaklaşık</span>}
            </span>
            <span className="mono">{n(city.orders)}</span>
          </li>
        ))}
        {towns.length > 12 ? <li className="sm-muted">{n(towns.length - 12)} şehir daha</li> : null}
      </ul>
      <h4 className="sm-side__h">En çok satan ürünler</h4>
      <ul className="sm-side__list">
        {country.topProducts.map((product) => (
          <li key={product.title}>
            <span>{product.title}</span>
            <span className="mono">{n(product.orders)}</span>
          </li>
        ))}
      </ul>
      <h4 className="sm-side__h">Son siparişler</h4>
      {orders.length === 0 ? (
        <p className="sm-muted">Son {n(view.recentOrders.length)} sipariş arasında yok.</p>
      ) : (
        <ul className="sm-side__list">
          {orders.map((order, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: orders carry no id on purpose; the list is short and fixed.
            <li key={`${order.day}-${index}`}>
              <span>
                {formatIsoDay(order.day)} · {order.city ?? 'şehir bilinmiyor'} · {order.title}
              </span>
              <span className="mono">{usd(order.totalCents)}</span>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

type SortKey = 'name' | 'orders' | 'revenue' | 'first' | 'last';

function CountryTable({
  view,
  selected,
  onSelect,
}: {
  view: SalesMapView;
  selected: string | null;
  onSelect: (iso2: string) => void;
}) {
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'revenue', desc: true });
  const rows = useMemo(() => {
    const value = (country: SalesMapCountry): string | number =>
      sort.key === 'name'
        ? country.name
        : sort.key === 'orders'
          ? country.orders
          : sort.key === 'revenue'
            ? country.revenueCents
            : sort.key === 'first'
              ? country.firstDay
              : country.lastDay;
    return [...view.countries].sort((a, b) => {
      const x = value(a);
      const y = value(b);
      const order = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), 'tr');
      return sort.desc ? -order : order;
    });
  }, [view, sort]);
  const max = Math.max(1, ...view.countries.map((country) => country.revenueCents));
  const header = (key: SortKey, label: string) => (
    <th scope="col" aria-sort={sort.key === key ? (sort.desc ? 'descending' : 'ascending') : 'none'}>
      <button
        type="button"
        className="sm-sort"
        onClick={() => setSort((prev) => ({ key, desc: prev.key === key ? !prev.desc : key !== 'name' }))}
      >
        {label}
        {sort.key === key ? <span aria-hidden="true">{sort.desc ? ' ↓' : ' ↑'}</span> : null}
      </button>
    </th>
  );
  return (
    <section className="au-card sm-section" aria-labelledby="sm-countries">
      <h3 className="au-card__name" id="sm-countries">
        Ülkeler
      </h3>
      <div className="sm-scroll">
        <table className="sx-table sm-table">
          <thead>
            <tr>
              {header('name', 'Ülke')}
              {header('orders', 'Sipariş')}
              {header('revenue', 'Gelir')}
              <th scope="col">Pay</th>
              {header('first', 'İlk')}
              {header('last', 'Son')}
            </tr>
          </thead>
          <tbody>
            {rows.map((country) => (
              <tr key={country.iso2} className={selected === country.iso2 ? 'sm-row--on' : undefined}>
                <th scope="row">
                  <button type="button" className="sm-linkish" onClick={() => onSelect(country.iso2)}>
                    {country.name}
                  </button>
                </th>
                <td className="mono">{n(country.orders)}</td>
                <td className="mono">{usd(country.revenueCents)}</td>
                <td>
                  <span className="sm-share" aria-hidden="true">
                    <span style={{ width: `${(country.revenueCents / max) * 100}%` }} />
                  </span>
                  <span className="sr-only">
                    %
                    {view.totals.revenueCents > 0
                      ? Math.round((country.revenueCents / view.totals.revenueCents) * 100)
                      : 0}
                  </span>
                </td>
                <td className="mono">{formatIsoDay(country.firstDay)}</td>
                <td className="mono">{formatIsoDay(country.lastDay)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RecentOrders({ view }: { view: SalesMapView }) {
  const names = new Map(view.countries.map((country) => [country.iso2, country.name]));
  return (
    <section className="au-card sm-section" aria-labelledby="sm-recent">
      <h3 className="au-card__name" id="sm-recent">
        Son siparişler
      </h3>
      {view.recentOrders.length === 0 ? (
        <p className="sm-muted">Bu dönemde sipariş yok.</p>
      ) : (
        <ul className="sm-orders">
          {view.recentOrders.map((order, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: orders carry no id on purpose (no receipt number is sent).
            <li key={`${order.day}-${index}`} className="sm-order">
              <span className="sm-order__day mono">{formatIsoDay(order.day)}</span>
              <span className="sm-order__body">
                <span className="sm-order__title">
                  {order.title}
                  {order.itemCount > 1 ? <span className="sm-muted"> +{order.itemCount - 1}</span> : null}
                </span>
                <span className="sm-muted">
                  <span className={`sm-swatch sm-swatch--${order.category}`} aria-hidden="true" />
                  {[
                    order.city,
                    order.region,
                    order.country === null ? null : (names.get(order.country) ?? order.country),
                  ]
                    .filter((part, i, all) => part !== null && all.indexOf(part) === i)
                    .join(', ') || 'yer bilinmiyor'}
                  {' · '}
                  {DOMINANT_LABELS[order.category]}
                </span>
              </span>
              <span className="sm-order__amount mono">{usd(order.totalCents)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** New countries month by month: a bar per month (orders), the new countries named under it. */
function Months({ view }: { view: SalesMapView }) {
  const months = view.months.slice(-12);
  if (months.length === 0) return null;
  const max = Math.max(1, ...months.map((month) => month.orders));
  const total = view.months[view.months.length - 1]?.countriesTotal ?? 0;
  return (
    <section className="au-card sm-section" aria-labelledby="sm-months">
      <h3 className="au-card__name" id="sm-months">
        Ulaşılan ülkeler
      </h3>
      <p className="au-card__purpose">
        Toplam {n(total)} ülke. Çubuk: ayın siparişi; altında o ay ilk kez sipariş veren ülkeler.
      </p>
      <ol className="sm-months">
        {months.map((month) => (
          <li key={month.month} className="sm-month">
            <span className="sm-month__bar" aria-hidden="true">
              <span style={{ height: `${(month.orders / max) * 100}%` }} />
            </span>
            <span className="sm-month__label mono">{month.month.slice(2).replace('-', '/')}</span>
            <span className="sm-month__new">{month.newCountries > 0 ? `+${month.newCountryCodes.join(' ')}` : ''}</span>
            <span className="sr-only">
              {`${month.month}: ${n(month.orders)} sipariş, ${n(month.newCountries)} yeni ülke, toplam ${n(month.countriesTotal)}`}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function CityTable({ view }: { view: SalesMapView }) {
  const names = new Map(view.countries.map((country) => [country.iso2, country.name]));
  return (
    <details className="au-card sm-section sm-cities">
      <summary className="au-card__name">Şehir listesi ({n(view.cities.length)})</summary>
      <div className="sm-scroll">
        <table className="sx-table sm-table">
          <caption className="sr-only">Haritadaki şehirler, sipariş sayısına göre</caption>
          <thead>
            <tr>
              <th scope="col">Şehir</th>
              <th scope="col">Ülke</th>
              <th scope="col">Sipariş</th>
              <th scope="col">Gelir</th>
              <th scope="col">Tür</th>
              <th scope="col">Son</th>
              <th scope="col">Konum</th>
            </tr>
          </thead>
          <tbody>
            {view.cities.map((city) => (
              <tr key={city.key}>
                <th scope="row">{placeText(city)}</th>
                <td>{names.get(city.country) ?? city.country}</td>
                <td className="mono">{n(city.orders)}</td>
                <td className="mono">{usd(city.revenueCents)}</td>
                <td>{DOMINANT_LABELS[city.dominant]}</td>
                <td className="mono">{formatIsoDay(city.lastDay)}</td>
                <td>{city.located ? 'şehir' : PRECISION_LABELS[city.precision]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

interface TileDelta {
  text: string;
  tone: 'up' | 'down' | 'flat';
}

function delta(now: number, before: number, show: (value: number) => string): TileDelta {
  const diff = now - before;
  if (diff === 0) return { text: '±0', tone: 'flat' };
  return { text: `${diff > 0 ? '+' : '−'}${show(Math.abs(diff))}`, tone: diff > 0 ? 'up' : 'down' };
}

function Tile({ label, value, change, base }: { label: string; value: string; change: TileDelta; base: string }) {
  return (
    <div className="kpi-tile sm-tile">
      <span className="kpi-tile__label">{label}</span>
      <span className="kpi-tile__value">
        <span className="mono">{value}</span>
      </span>
      <span className="kpi-tile__compare">
        <span className={`kpi-tile__delta kpi-tile__delta--${change.tone}`}>{change.text}</span>
        <span className="kpi-tile__base">{base}</span>
      </span>
    </div>
  );
}

export function Tiles({ view }: { view: SalesMapView }) {
  const { totals, previous, thisMonth } = view;
  // Tümü: what this month added (the portal's "+1 bu ay"); a window: against the same number of days before it.
  const base = previous === null ? 'bu ay' : `önceki ${view.period} güne göre`;
  const pick = (now: number, before: number | undefined, month: number, show: (v: number) => string): TileDelta =>
    previous === null || before === undefined
      ? { text: `+${show(month)}`, tone: month > 0 ? 'up' : 'flat' }
      : delta(now, before, show);
  return (
    <section className="sm-tiles" aria-label="Dönem özeti">
      <Tile
        label="Ülke"
        value={n(totals.countries)}
        change={pick(totals.countries, previous?.countries, thisMonth.newCountries, n)}
        base={base}
      />
      <Tile
        label="Şehir"
        value={n(totals.cities)}
        change={pick(totals.cities, previous?.cities, thisMonth.newCities, n)}
        base={base}
      />
      <Tile
        label="Sipariş"
        value={n(totals.orders)}
        change={pick(totals.orders, previous?.orders, thisMonth.orders, n)}
        base={base}
      />
      <Tile
        label="Gelir"
        value={usd(totals.revenueCents)}
        change={pick(totals.revenueCents, previous?.revenueCents, thisMonth.revenueCents, usd)}
        base={base}
      />
    </section>
  );
}

const PERIOD_OPTIONS = SALES_MAP_PERIODS.map((id) => ({ id, label: PERIOD_LABELS[id], count: null }));

function Notes({ view }: { view: SalesMapView }) {
  const parts: string[] = [];
  if (view.unlocatedOrders > 0) {
    parts.push(
      `${n(view.unlocatedOrders)} siparişin şehri haritada bulunamadı; eyalet ya da ülke ortasında gösterilir.`,
    );
  }
  if (view.noCityOrders > 0) {
    parts.push(
      `${n(view.noCityOrders)} siparişin şehri henüz okunmadı (eski siparişler günlük Etsy okumasında bir kez tamamlanır).`,
    );
  }
  if (parts.length === 0) return null;
  return <p className="sm-muted sm-notes">{parts.join(' ')}</p>;
}

export function SalesMapPage() {
  const { store, state } = useSalesMap();
  const desktop = useIsDesktop();
  const [selected, setSelected] = useState<string | null>(null);
  useEffect(() => {
    void store.reload();
  }, [store]);
  const view: SalesMapView | null = state.views[salesMapKey(state.period, state.category)] ?? null;
  // A country stays chosen until the panel's close button (a second tap on a bubble must not close it).
  const select = (iso2: string) => setSelected(iso2);
  // On a phone the country panel is a sheet under the map: bring it into view when a country is chosen.
  useEffect(() => {
    if (desktop || selected === null) return;
    document.getElementById('sm-side')?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [desktop, selected]);
  return (
    <div className={desktop ? 'au au--desk sm' : 'au sm'}>
      <div className="sm-toolbar">
        <Segmented
          label="Dönem"
          options={PERIOD_OPTIONS}
          value={state.period}
          onChange={(period) => void store.setPeriod(period)}
          controls="sm-panel"
        />
        <fieldset className="sm-chips">
          <legend className="sr-only">Ürün türü</legend>
          {SALES_MAP_CATEGORY_FILTERS.map((category) => (
            <button
              key={category}
              type="button"
              className={`sm-chip${category === 'all' ? '' : ` sm-chip--${category satisfies SalesMapCategoryFilter}`}`}
              aria-pressed={state.category === category}
              onClick={() => void store.setCategory(category)}
            >
              {category === 'all' ? null : (
                <span className={`sm-swatch sm-swatch--${category as SalesCategory}`} aria-hidden="true" />
              )}
              {CATEGORY_LABELS[category]}
            </button>
          ))}
        </fieldset>
        <button
          type="button"
          className="btn btn--secondary"
          disabled={state.loading}
          onClick={() => void store.reload()}
        >
          {state.loading ? 'Yenileniyor…' : 'Yenile'}
        </button>
      </div>
      {state.error === null ? null : (
        <p className="shop-status" role="status">
          {view === null ? 'Satış haritası yüklenemedi' : 'Yenilenemedi'}: {state.error}{' '}
          <button type="button" className="btn btn--secondary" onClick={() => void store.reload()}>
            Tekrar dene
          </button>
        </p>
      )}
      <div id="sm-panel" role="tabpanel" aria-labelledby={`tab-sm-panel-${state.period}`} className="sm-panel">
        {view === null ? (
          state.error === null ? (
            <div className="loading" role="status">
              Yükleniyor…
            </div>
          ) : null
        ) : (
          <>
            <Tiles view={view} />
            {view.range === null ? null : (
              <p className="sm-muted">
                {formatIsoDay(view.range.from)} - {formatIsoDay(view.range.to)}; önceki dönem{' '}
                {view.previousRange === null
                  ? ''
                  : `${formatIsoDay(view.previousRange.from)} - ${formatIsoDay(view.previousRange.to)}`}
              </p>
            )}
            <div className="sm-main">
              <section className="au-card sm-mapcard" aria-label="Harita">
                {view.countries.length === 0 ? (
                  <p className="sm-muted">Bu dönemde ve bu ürün türünde sipariş yok.</p>
                ) : (
                  <SalesWorldMap view={view} selected={selected} onSelect={select} />
                )}
                <Legend view={view} />
                <Notes view={view} />
              </section>
              <CountryPanel view={view} iso2={selected} onClose={() => setSelected(null)} onSelect={select} />
            </div>
            <div className="sm-two">
              <CountryTable view={view} selected={selected} onSelect={select} />
              <RecentOrders view={view} />
            </div>
            <Months view={view} />
            <CityTable view={view} />
          </>
        )}
      </div>
    </div>
  );
}
